import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { ArrowLeft, MapPin, AlertCircle, ExternalLink, Filter, PlusCircle } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import TopNav from '../components/sections/TopNav';
import SiteFooter from '../components/sections/SiteFooter';
import SubmitTipButton from '../components/SubmitTipButton';
import SubmitIncidentModal from '../components/SubmitIncidentModal';
import MapLegend from '../components/MapLegend';
import { useJsonLd, buildBreadcrumb } from '../utils/jsonLd';
import PageSeoTag from '../components/PageSeoTag';

const API = process.env.REACT_APP_BACKEND_URL;
const MELBOURNE_CENTER = [-37.8136, 144.9631];

// Normalise both new LLM categories (snake_case) and legacy admin ones (Title Case).
const CATEGORY_META = {
  shooting:    { emoji: '🔫', color: '#ef4444', label: 'Shooting' },
  firebombing: { emoji: '🧨', color: '#fb923c', label: 'Firebombing' },
  arson:       { emoji: '🔥', color: '#f97316', label: 'Arson' },
  murder:      { emoji: '💀', color: '#e11d48', label: 'Murder' },
  assault:     { emoji: '👊', color: '#f59e0b', label: 'Assault' },
  stabbing:    { emoji: '🔪', color: '#f43f5e', label: 'Stabbing' },
  kidnapping:  { emoji: '🚗', color: '#eab308', label: 'Kidnapping' },
  drug_bust:   { emoji: '💊', color: '#a855f7', label: 'Drug Bust' },
  arrest:      { emoji: '👮', color: '#3b82f6', label: 'Arrest' },
  raid:        { emoji: '🚨', color: '#2563eb', label: 'Police Raid' },
  robbery:     { emoji: '🎭', color: '#c084fc', label: 'Robbery' },
  vehicle:     { emoji: '🏎️', color: '#facc15', label: 'Vehicle' },
  court:       { emoji: '⚖️', color: '#d4d4d8', label: 'Court' },
  prison:      { emoji: '🔒', color: '#71717a', label: 'Prison' },
  gangland:    { emoji: '⚔️', color: '#dc2626', label: 'Gangland' },
  other:       { emoji: '📍', color: '#94a3b8', label: 'Other' },
};

// Legacy Title Case values already in the DB before this feature shipped.
const LEGACY_ALIASES = {
  'Shooting': 'shooting', 'Stabbing': 'stabbing', 'Arson': 'arson',
  'Bombing': 'firebombing', 'Tobacco War': 'gangland', 'Drug Bust': 'drug_bust',
  'Gangland': 'gangland', 'Corruption': 'court', 'Other': 'other',
};

const normalizeCategory = (raw) => {
  if (!raw) return 'other';
  if (CATEGORY_META[raw]) return raw;
  if (LEGACY_ALIASES[raw]) return LEGACY_ALIASES[raw];
  const lower = String(raw).toLowerCase().replace(/\s+/g, '_');
  return CATEGORY_META[lower] ? lower : 'other';
};

const iconCache = new Map();
const buildIcon = (categoryKey) => {
  if (iconCache.has(categoryKey)) return iconCache.get(categoryKey);
  const meta = CATEGORY_META[categoryKey] || CATEGORY_META.other;
  const html = `
    <div class="crime-marker" style="--marker-color:${meta.color}">
      <div class="crime-marker__glow"></div>
      <div class="crime-marker__bubble">
        <span class="crime-marker__emoji">${meta.emoji}</span>
      </div>
      <div class="crime-marker__stem"></div>
    </div>
  `;
  const icon = L.divIcon({
    className: 'crime-marker-wrap',
    html,
    iconSize: [40, 52],
    iconAnchor: [20, 48],
    popupAnchor: [0, -44],
  });
  iconCache.set(categoryKey, icon);
  return icon;
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
    timeZone: 'Australia/Melbourne',
  });
};

// Auto-fit map to all markers if there are any
const FitBounds = ({ markers }) => {
  const map = useMap();
  useEffect(() => {
    if (!markers || markers.length === 0) return;
    const bounds = markers.map((m) => [m.lat, m.lng]);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
  }, [markers, map]);
  return null;
};

const CrimeMap = () => {
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [submitOpen, setSubmitOpen] = useState(false);

  useJsonLd(buildBreadcrumb([
    { name: 'Home', url: 'https://www.outlawmedia.com.au/' },
    { name: 'Gangland Map', url: 'https://www.outlawmedia.com.au/ganglandmap' },
  ]), 'map-breadcrumb');

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/crime-map/markers`, { cache: 'no-store' })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => { if (!cancelled) setMarkers(Array.isArray(data) ? data : []); })
      .catch((e) => { if (!cancelled) setError(e.message || 'Could not load markers.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const categories = useMemo(() => {
    const set = new Set(markers.map((m) => normalizeCategory(m.category)));
    return ['All', ...Array.from(set)];
  }, [markers]);

  const visible = activeCategory === 'All'
    ? markers
    : markers.filter((m) => normalizeCategory(m.category) === activeCategory);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="crime-map-page">
      <TopNav />
      <PageSeoTag pageKey="map" />

      <header className="relative pt-8 pb-6 px-4 border-b border-zinc-800 overflow-hidden">
        <div className="relative max-w-5xl mx-auto">
          <Link
            to="/"
            data-testid="map-back-link"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-gray-400 hover:text-red-400 transition-colors mb-3"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
          </Link>
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-red-400 mb-1">
                Crime Map
              </p>
              <h1 className="text-3xl md:text-4xl font-black tracking-tight leading-tight">
                Australia's <span className="text-red-500">Underworld</span>, Mapped
              </h1>
              <p className="mt-2 text-sm text-gray-400 max-w-2xl">
                Every incident we've covered - pinned, dated, and linked back to the original
                investigation. Tap a marker for the story.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-wider uppercase text-gray-400">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                {visible.length} marker{visible.length === 1 ? '' : 's'}
              </span>
              <button
                type="button"
                onClick={() => setSubmitOpen(true)}
                data-testid="crime-map-submit-btn"
                className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-500 text-white text-[11px] font-bold uppercase tracking-wider rounded px-3 py-1.5 transition-colors"
              >
                <PlusCircle className="h-3 w-3" /> Submit your own
              </button>
            </div>
          </div>

          {categories.length > 2 && (
            <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="map-category-filters">
              <Filter className="h-3 w-3 text-gray-500" />
              {categories.map((c) => {
                const label = c === 'All' ? 'All' : (CATEGORY_META[c]?.label || c);
                const emoji = c === 'All' ? '' : (CATEGORY_META[c]?.emoji || '');
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setActiveCategory(c)}
                    data-testid={`map-filter-${String(c).toLowerCase().replace(/\s+/g, '-')}`}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border transition-colors ${
                      activeCategory === c
                        ? 'bg-red-600 text-white border-red-500'
                        : 'bg-zinc-900/60 text-gray-400 border-zinc-800 hover:border-red-500/40 hover:text-white'
                    }`}
                  >
                    {emoji && <span aria-hidden="true">{emoji}</span>}
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </header>

      <main className="px-2 sm:px-4 py-4">
        <div className="max-w-5xl mx-auto">
          {error && (
            <div className="bg-red-950/40 border border-red-500/50 text-red-200 px-3 py-2 rounded text-sm mb-4 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          <div
            className="relative rounded-xl overflow-hidden border border-red-900/40 shadow-[0_30px_60px_-20px_rgba(220,38,38,0.25)]"
            style={{ height: 'min(70vh, 620px)' }}
            data-testid="crime-map-container"
          >
            {loading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 text-gray-400 text-sm">
                Loading map…
              </div>
            )}

            <MapContainer
              center={MELBOURNE_CENTER}
              zoom={11}
              scrollWheelZoom
              style={{ height: '100%', width: '100%', background: '#000' }}
              className="crimemap-dark-tiles"
            >
              {/* OSM standard raster tiles - free, no API key, no signup.
                  Darkened via CSS filter on `.crimemap-dark-tiles` below so
                  we keep the site's gritty look without a paid provider. */}
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={19}
              />
              <FitBounds markers={visible} />
              {visible.map((m) => {
                const catKey = normalizeCategory(m.category);
                const meta = CATEGORY_META[catKey] || CATEGORY_META.other;
                return (
                  <Marker
                    key={m.id}
                    position={[m.lat, m.lng]}
                    icon={buildIcon(catKey)}
                  >
                    <Popup>
                      <div style={{ minWidth: 200, color: '#0a0a0a' }}>
                        <div style={{ fontSize: 10, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.15em', color: meta.color, marginBottom: 4 }}>
                          <span style={{ marginRight: 4 }}>{meta.emoji}</span>
                          {meta.label}{m.event_date ? ` · ${formatDate(m.event_date)}` : ''}
                        </div>
                        <div style={{ fontWeight: 800, marginBottom: 6, fontSize: 13, lineHeight: 1.25 }}>{m.title}</div>
                        {m.summary && (
                          <p style={{ fontSize: 12, lineHeight: 1.35, margin: 0, marginBottom: 6 }}>{m.summary}</p>
                        )}
                        {m.story_url && (
                          <a
                            href={m.story_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 800, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.15em' }}
                          >
                            Read story <ExternalLink size={11} />
                          </a>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>

            {/* Corner legend - decodes the emoji markers for first-time
                visitors. Only lists categories actually present in the
                current filter view so we never advertise pins that aren't
                on the map. Collapsible for mobile. */}
            <MapLegend
              categoriesMeta={CATEGORY_META}
              visibleKeys={Array.from(new Set(visible.map((m) => normalizeCategory(m.category))))}
            />

            {!loading && markers.length === 0 && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 px-6 text-center" data-testid="map-empty-state">
                <div>
                  <MapPin className="h-8 w-8 text-red-500 mx-auto mb-2" />
                  <p className="text-sm font-black uppercase tracking-widest text-red-300 mb-1">Map under construction</p>
                  <p className="text-xs text-gray-400 max-w-sm">
                    Admin → <span className="text-red-400">Crime Map</span> tab to drop pins, or flip the kill-switch
                    if you'd rather not run the map.
                  </p>
                </div>
              </div>
            )}
          </div>

          <p className="text-[10px] text-gray-600 tracking-widest uppercase text-center mt-3">
            Tiles · CARTO Dark · Data · Outlaw Media investigations
          </p>
        </div>
      </main>

      <SiteFooter />
      <SubmitTipButton />
      <SubmitIncidentModal open={submitOpen} onClose={() => setSubmitOpen(false)} />
    </div>
  );
};

export default CrimeMap;
